import { motion } from "framer-motion";
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { majorworks } from "../data/projects";
import { useTransitionOverlay } from "../context/TransitionContext";

function ProjectList() {
  const scrollElementsRef = useRef([]);
  const navigate = useNavigate();
  const { startTransition } = useTransitionOverlay();

  const handleClick = (index, e) => {
    e.preventDefault();

    const el = scrollElementsRef.current[index];
    const rect = el ? el.getBoundingClientRect() : null;

    if (rect && rect.width > 0 && rect.height > 0) {
      startTransition({
        src: majorworks[index].image,
        rect: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
      });
    }

    navigate(`/project/${majorworks[index].url}`);
  };

  return (
    <Section>
      {majorworks?.map((item, index) => (
        <Wrapper key={index}>
          <MobileImg src={item.image} alt={item.name} />

          <ImgWrapper ref={(el) => (scrollElementsRef.current[index] = el)}>
            <Img alt={item.name} src={item.image} />
          </ImgWrapper>

          <Container>
            <FirstContainer>
              <div style={{ overflow: "hidden" }}>
                <motion.h5>{String(index + 1).padStart(2, "0")}</motion.h5>
              </div>
              <div>
                <div style={{ overflow: "hidden" }}>
                  <H3>{item.name}</H3>
                </div>
                <div style={{ overflow: "hidden" }}>
                  <Span>{item.type}</Span>
                </div>
              </div>
            </FirstContainer>

            <div style={{ overflow: "hidden" }}>
              <PWrapperBt>
                <Anchor
                  href={`/project/${item.url}`}
                  onClick={(e) => handleClick(index, e)}
                >
                  View Project
                  <svg width="30" height="18" viewBox="0 0 10 6" fill="none">
                    <path
                      d="M6.71627 0L6.27434 0.441934L8.14928 2.31688H0V2.94189H8.14922L6.27434 4.81678L6.71627 5.25871L9.34566 2.62936L6.71627 0Z"
                      fill="currentColor"
                    />
                  </svg>
                </Anchor>
              </PWrapperBt>
            </div>
          </Container>
        </Wrapper>
      ))}
    </Section>
  );
}

export default ProjectList;

const Section = styled.div`
  overflow: hidden;
`;

const Container = styled.div`
  width: 100%;
  margin: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 65px 0;

  @media (max-width: 1430px) {
    padding: 50px 30px;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 20px 28px;
    gap: 16px;
  }
`;

const Anchor = styled.a`
  text-decoration: none;
  font-size: 16px;
  text-transform: capitalize;
  position: relative;
  background: none;
  border: none;
  outline: none;
  color: var(--text);
  display: flex;
  padding: 16px 32px;
  z-index: 1;
  width: fit-content;
  align-items: center;
  gap: 16px;
  transition: color 0.3s ease;
  cursor: pointer;

  &::after {
    position: absolute;
    content: "";
    width: 46px;
    height: 46px;
    background: var(--btn-bg);
    z-index: -1;
    display: block;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto 0;
    transition: 0.8s ease;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    padding: 14px 24px;
    margin-top: 4px;
  }
`;

const PWrapperBt = styled(motion.div)`
  overflow: hidden;
`;

const H3 = styled(motion.h3)`
  font-size: 40px;
  transition: color 0.3s ease, font-size 0.5s ease;
  color: var(--text-6);

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Span = styled(motion.div)`
  font-size: 14px;
  transition: color 0.3s ease, font-size 0.5s ease;
  color: var(--text-6);
  margin-top: 4px;
`;

const ImgWrapper = styled.div`
  width: 461px;
  height: 300px;
  position: absolute;
  right: 23px;
  transition: 0.5s ease;
  cursor: pointer;

  &::before {
    width: 0;
    height: 210px;
    transition: 0.5s ease;
    content: "";
    right: 0;
    top: 148px;
    background: var(--text);
    position: absolute;
    display: inline-block;
    transition-delay: 0.1s;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Img = styled.img`
  width: 0;
  height: 100%;
  transition: 0.5s ease;
  transform: perspective(500px) rotateX(0deg) rotateY(-18deg) rotateZ(0deg);
  filter: grayscale(1);
  display: block;
`;

const MobileImg = styled.img`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
    filter: grayscale(0.2);
  }
`;

const FirstContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  &::before {
    content: "";
    width: 150px;
    transition: 0.5s ease;
    height: 1px;
    background: var(--text);
  }

  @media (max-width: 768px) {
    &::before {
      width: 40px;
    }
  }
`;

const Wrapper = styled.div`
  background-color: var(--bg-card);
  color: var(--text);
  transition: background-color 0.3s ease, color 0.3s ease;
  border-bottom: 1px solid var(--border);
  position: relative;

  &:hover {
    background-color: var(--bg-alt);
  }

  /* Desktop hover effects */
  @media (min-width: 769px) {
    &:hover ${Anchor}::after {
      width: 210px;
      height: 100%;
      border-radius: 40px;
    }
    &:hover ${Img} {
      width: 430px;
      filter: grayscale(0);
    }
    &:hover ${ImgWrapper} {
      width: 430px;
      &::before {
        width: 390px;
        right: 100px;
      }
    }
    &:hover ${Span} {
      font-size: 18px;
      color: var(--text);
    }
    &:hover ${H3} {
      font-size: 70px;
      color: var(--text);
    }
    &:hover ${FirstContainer}::before {
      width: 200px;
    }
  }
`;
